import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number
}

// AR-specific fixed prices (in ARS)
const AR_FIXED_PRICES: Record<number, number> = {
  13.99: 19990,
  47: 39980,
}

const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  AR: { code: 'ARS', symbol: '$', rate: 1445 },
  CL: { code: 'CLP', symbol: '$', rate: 1144 },
  MX: { code: 'MXN', symbol: '$', rate: 22.5 },
  CO: { code: 'COP', symbol: '$', rate: 4325 },
  PE: { code: 'PEN', symbol: 'S/', rate: 4.52 },
  UY: { code: 'UYU', symbol: '$', rate: 48.17 },
  PY: { code: 'PYG', symbol: '₲', rate: 9031 },
  BO: { code: 'BOB', symbol: 'Bs', rate: 8.3 },
  CR: { code: 'CRC', symbol: '₡', rate: 614 },
  GT: { code: 'GTQ', symbol: 'Q', rate: 9.39 },
  HN: { code: 'HNL', symbol: 'L', rate: 30.1 },
  NI: { code: 'NIO', symbol: 'C$', rate: 43.95 },
  DO: { code: 'DOP', symbol: 'RD$', rate: 69.84 },
  ES: { code: 'EUR', symbol: '€', rate: 1.11 },
  BR: { code: 'BRL', symbol: 'R$', rate: 6.15 },
  EC: { code: 'USD', symbol: '$', rate: 1 },
  PA: { code: 'USD', symbol: '$', rate: 1 },
  SV: { code: 'USD', symbol: '$', rate: 1 },
  VE: { code: 'USD', symbol: '$', rate: 1 },
  US: { code: 'USD', symbol: '$', rate: 1 },
}

const DEFAULT_CURRENCY: CurrencyInfo = { code: 'USD', symbol: '$', rate: 1 }

function getFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '🌎'
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

function applyCharmPricing(amount: number): number {
  if (amount >= 100000) return Math.floor(amount / 1000) * 1000 - 100
  if (amount >= 10000) return Math.floor(amount / 1000) * 1000 - 100
  if (amount >= 1000) return Math.floor(amount / 100) * 100 - 10
  if (amount >= 100) return Math.floor(amount / 10) * 10 - 1
  if (amount >= 10) return Math.floor(amount) - 0.01
  return Math.floor(amount) + 0.99
}

export function useLocalCurrency() {
  const [countryCode, setCountryCode] = useState<string>('US')
  const [currencyInfo, setCurrencyInfo] =
    useState<CurrencyInfo>(DEFAULT_CURRENCY)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initCurrency = async () => {
      try {
        const cached = sessionStorage.getItem('mp_visitor_country')
        if (cached) {
          const parsed = JSON.parse(cached)
          setCountryCode(parsed.countryCode)
          setCurrencyInfo(CURRENCY_MAP[parsed.countryCode] || DEFAULT_CURRENCY)
          setIsLoading(false)
          return
        }

        const response = await fetch('https://ipapi.co/json/')
        if (response.ok) {
          const data = await response.json()
          const code = data.country_code
          setCountryCode(code)
          setCurrencyInfo(CURRENCY_MAP[code] || DEFAULT_CURRENCY)
          sessionStorage.setItem(
            'mp_visitor_country',
            JSON.stringify({ countryCode: code }),
          )
        } else {
          throw new Error('API failed')
        }
      } catch (error) {
        const lang = navigator.language || 'es-US'
        const parts = lang.split('-')
        const code = parts.length > 1 ? parts[1].toUpperCase() : 'US'
        setCountryCode(code)
        setCurrencyInfo(CURRENCY_MAP[code] || DEFAULT_CURRENCY)
      } finally {
        setIsLoading(false)
      }
    }

    initCurrency()
  }, [])

  const isUSD = currencyInfo.code === 'USD'
  const isAR = countryCode === 'AR'

  const formatPrice = (usdAmount: number): string => {
    if (isUSD) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(usdAmount)
    }

    // AR fixed prices override
    if (isAR && AR_FIXED_PRICES[usdAmount] !== undefined) {
      const fixed = AR_FIXED_PRICES[usdAmount]
      const formatted = new Intl.NumberFormat('es-AR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(fixed)
      return `${currencyInfo.symbol}${formatted} ${currencyInfo.code}`
    }

    const converted = usdAmount * currencyInfo.rate
    const charmed = applyCharmPricing(converted)
    const useFractions = charmed < 100 && charmed % 1 !== 0

    const formatted = new Intl.NumberFormat('es-AR', {
      style: 'decimal',
      minimumFractionDigits: useFractions ? 2 : 0,
      maximumFractionDigits: useFractions ? 2 : 0,
    }).format(charmed)

    return `${currencyInfo.symbol}${formatted} ${currencyInfo.code}`
  }

  const formatPriceCompact = (usdAmount: number): string => {
    if (isUSD) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
      }).format(usdAmount)
    }

    // AR fixed prices override
    if (isAR && AR_FIXED_PRICES[usdAmount] !== undefined) {
      const fixed = AR_FIXED_PRICES[usdAmount]
      if (fixed >= 1000) {
        const k = (fixed / 1000).toFixed(1).replace(/\.0$/, '')
        return `${currencyInfo.symbol}${k}k`
      }
      return `${currencyInfo.symbol}${fixed}`
    }

    // AR proportional for non-fixed amounts
    if (isAR) {
      const arRate = 19990 / 13.99
      const converted = usdAmount * arRate
      const rounded = Math.round(converted / 100) * 100
      if (rounded >= 1000) {
        const k = (rounded / 1000).toFixed(1).replace(/\.0$/, '')
        return `${currencyInfo.symbol}${k}k`
      }
      return `${currencyInfo.symbol}${rounded}`
    }

    const converted = usdAmount * currencyInfo.rate
    const charmed = applyCharmPricing(converted)

    if (charmed >= 1000) {
      const k = (charmed / 1000).toFixed(1).replace(/\.0$/, '')
      return `${currencyInfo.symbol}${k}k`
    }

    const formatted = new Intl.NumberFormat('es-AR', {
      style: 'decimal',
      maximumFractionDigits: charmed < 100 ? 2 : 0,
    }).format(charmed)

    return `${currencyInfo.symbol}${formatted}`
  }

  const checkoutUrl =
    countryCode === 'AR'
      ? 'http://checkout.sabores365.online/cart/59169162362961:1'
      : 'https://pay.hotmart.com/P104026694B?checkoutMode=10'

  return {
    formatPrice,
    formatPriceCompact,
    currencyCode: currencyInfo.code,
    currencySymbol: currencyInfo.symbol,
    countryCode,
    countryFlag: getFlagEmoji(countryCode),
    isUSD,
    isLoading,
    checkoutUrl,
  }
}
